import { nationalityName } from '@felix/domain';
import {
  ActionIcon,
  Button,
  Menu,
  Pagination,
  Select,
  Skeleton,
  TextInput,
  UnstyledButton,
} from '@mantine/core';
import {
  IconAnchor,
  IconCertificate,
  IconChevronDown,
  IconChevronUp,
  IconClockHour4,
  IconDots,
  IconFilterOff,
  IconHourglassHigh,
  IconPlus,
  IconSearch,
  IconSelector,
  IconUser,
  IconUsers,
  IconUsersGroup,
} from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { formatDay } from '../../format.js';
import { PageHeader } from '../../PageHeader.js';
import { Panel } from '../../Panel.js';
import { PersonAvatar } from '../../PersonAvatar.js';
import { StatTile } from '../../StatTile.js';
import { StatusBadge } from '../../StatusBadge.js';
import { useShell } from '../../shell-context.js';
import { ShipGlyph } from '../../VesselBadge.js';
import { CrewCardDialog } from './CrewCardDialog.js';
import classes from './CrewListScreen.module.css';
import {
  CREW_PAGE_SIZE,
  type CrewFilter,
  type CrewLoginFilter,
  type CrewSort,
  canAddCrew,
  contractBadge,
  crewRanks,
  LONG_ASHORE_DAYS,
  loginState,
  pageOf,
  type SortDir,
  summarizeCrew,
  viewCrew,
} from './crew.js';
import { errorCode, type PersonnelSource } from './source.js';
import type { CrewMember, PersonnelVessel } from './types.js';

type State =
  | { kind: 'loading' }
  | { kind: 'error'; code: string }
  | {
      kind: 'ready';
      today: string;
      crew: CrewMember[];
      vessels: PersonnelVessel[];
      /** New Crew Card ▸ "Sign on now" (ADR 0131 Ek 4 §2). */
      canSignOn: boolean;
      /** "Sign on now"da kayıtlı maaş alanı (`payroll.create`; ADR 0165 §3). */
      canSetWage: boolean;
      /** "+ Add Crew" (`users.create`; ortak 2026-10-30). */
      canCreate: boolean;
      /** New Crew Card ▸ "Create login" (`users.create` + `users.edit`; ADR 0151 Ek). */
      canCreateLogin: boolean;
    };

/** Yükleniyor iskeletindeki satır sayısı (M6f G2-07). */
const SKELETON_ROWS = 6;

/** Konum sorgusundaki kişi (`?user=<kimlik>`; pano uyarısının "View" bağlantısı, ADR 0033 eki). */
export function queryUser(search: string | undefined): string | null {
  return new URLSearchParams(search ?? '').get('user');
}

/**
 * Personel listesi (`gemi-personel.jpeg` panel 2 "Crew List", BGM düzeni; ADR 0012 §5, ADR
 * 0019): süzgeç satırı ve tablo tek kartta; varsayılan sıra rütbe kıdemi (M6f G2-01). Gemide
 * yalnız bu geminin bugün görevli personeli listelenir; özet kutucukları, gemi ve durum süzgeci
 * ile "Add Crew" yalnız web ofiste (atamayı ofis yönetir, ADR 0012). "+ Add Crew" personel kartını
 * açar (ADR 0131 §1); ad Crew Profile'a götürür; uyruk karttan.
 */
export function CrewListScreen({ source }: { source: PersonnelSource }) {
  const { t, i18n } = useTranslation();
  const shell = useShell();
  const locale = i18n.resolvedLanguage ?? 'en';
  const web = source.scope === 'web';
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [filter, setFilter] = useState<CrewFilter>('all');
  const [vesselId, setVesselId] = useState<string | null>(null);
  const [rank, setRank] = useState<string | null>(null);
  const [login, setLogin] = useState<CrewLoginFilter>('all');
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<{ key: CrewSort; dir: SortDir }>({ key: 'rank', dir: 'asc' });
  const focusUser = queryUser(shell?.search);
  // 0: kullanıcı henüz sayfa seçmedi; `?user=` ile gelince o kişinin sayfası açılır
  const [page, setPage] = useState(focusUser ? 0 : 1);
  const [adding, setAdding] = useState(false);
  const editor = web ? source.crewCards : undefined;

  const load = useCallback(() => {
    setState({ kind: 'loading' });
    Promise.all([source.crew(), source.vessels()])
      .then(([result, vessels]) =>
        setState({
          kind: 'ready',
          today: result.today,
          crew: result.crew,
          vessels,
          canSignOn: result.canSignOn === true,
          canSetWage: result.canSetWage === true,
          canCreate: canAddCrew(true, result),
          // Eski sunucu göndermez: anahtar eskisi gibi (sunucu yine denetler)
          canCreateLogin: result.canCreateLogin !== false,
        }),
      )
      .catch((error: unknown) => setState({ kind: 'error', code: errorCode(error) }));
  }, [source]);
  useEffect(load, [load]);

  const crew = state.kind === 'ready' ? state.crew : [];
  const vessels = state.kind === 'ready' ? state.vessels : [];
  const summary = useMemo(() => summarizeCrew(crew), [crew]);
  const ranks = useMemo(() => crewRanks(crew, locale), [crew, locale]);
  const view = useMemo(
    () =>
      viewCrew(crew, {
        filter,
        query,
        sort: sort.key,
        dir: sort.dir,
        locale,
        vesselId,
        rank,
        login,
      }),
    [crew, filter, query, sort, locale, vesselId, rank, login],
  );
  // `?user=` ile gelen kişinin satırı vurgulanır
  const focusIndex = focusUser ? view.findIndex((member) => member.userId === focusUser) : -1;
  const pages = Math.max(1, Math.ceil(view.length / CREW_PAGE_SIZE));
  const requested =
    page === 0 ? (focusIndex >= 0 ? Math.floor(focusIndex / CREW_PAGE_SIZE) + 1 : 1) : page;
  const current = Math.min(Math.max(requested, 1), pages);
  const visible = pageOf(view, current);
  const reset = () => setPage(1);
  const filtered =
    filter !== 'all' ||
    vesselId !== null ||
    rank !== null ||
    login !== 'all' ||
    query.trim() !== '';
  const clearFilters = () => {
    setFilter('all');
    setLogin('all');
    setVesselId(null);
    setRank(null);
    setQuery('');
    reset();
  };
  // Sorgu değişince (ör. bildirimden başka kişi) yeniden o kişinin sayfası
  useEffect(() => {
    if (focusUser) setPage(0);
  }, [focusUser]);

  const shipName = web ? null : (vessels[0]?.name ?? null);
  const status = (member: CrewMember) =>
    member.status === 'not_assigned' ? (
      <StatusBadge tone="neutral" outlined>
        {t('crew.status.not_assigned')}
      </StatusBadge>
    ) : member.status === 'on_board' ? (
      <StatusBadge tone="success" outlined>
        {t('crew.status.on_board')}
      </StatusBadge>
    ) : (
      <StatusBadge tone={(member.days ?? 0) > LONG_ASHORE_DAYS ? 'warning' : 'info'} outlined>
        {t('crew.status.ashore')}
      </StatusBadge>
    );
  // "Login" sütunu (ortak 2026-10-30): hesap var, yok ya da devre dışı
  const loginBadge = (member: CrewMember) => {
    const state = loginState(member);
    return (
      <StatusBadge
        tone={state === 'login' ? 'success' : state === 'disabled' ? 'danger' : 'neutral'}
        variant="dot"
      >
        {t(`crew.login.${state}`)}
      </StatusBadge>
    );
  };
  const duration = (member: CrewMember) =>
    member.days === null ? '—' : t('crew.days', { count: member.days });
  // Kontrat bitişi ve kalan gün rozeti (ADR 0076 §5): tarihi MLC sınırı belirliyorsa (kontrat
  // girilmemiş ya da sınırı aşıyor) üst satır MLC sınırıdır
  const contract = (member: CrewMember) => {
    const value = member.contract;
    const badge = contractBadge(value);
    if (!value || !badge) return '—';
    const title = [
      value.contractEnd
        ? `${t('crew.columns.contract')}: ${formatDay(value.contractEnd)}`
        : `${t('crew.columns.contract')}: ${t('crew.contract.noContract')}`,
      t('crew.contract.mlcLimit', { date: formatDay(value.mlcLimit) }),
      ...(value.exceedsMlc ? [t('crew.contract.exceeds')] : []),
    ].join(' · ');
    return (
      <span className={classes.contract} title={title}>
        <span className={classes.date}>
          {value.basis === 'contract' && value.contractEnd
            ? formatDay(value.contractEnd)
            : t('crew.contract.mlcLimit', { date: formatDay(value.mlcLimit) })}
        </span>
        <StatusBadge tone={badge.tone} withIcon={badge.tone !== 'neutral'}>
          {t(badge.key, { count: badge.count })}
        </StatusBadge>
      </span>
    );
  };

  const ariaSort = (column: CrewSort) =>
    sort.key === column ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined;
  const sortHeader = (column: CrewSort, label: string) => {
    const active = sort.key === column;
    const Icon = !active ? IconSelector : sort.dir === 'asc' ? IconChevronUp : IconChevronDown;
    return (
      <UnstyledButton
        className={classes.sort}
        data-active={active || undefined}
        onClick={() => {
          setSort((previous) =>
            previous.key === column
              ? { key: column, dir: previous.dir === 'asc' ? 'desc' : 'asc' }
              : { key: column, dir: 'asc' },
          );
          reset();
        }}
        aria-label={t('crew.sortBy', { column: label })}
      >
        <span>{label}</span>
        <Icon size={14} stroke={2} className={classes.sortIcon} aria-hidden />
      </UnstyledButton>
    );
  };
  const open = (path: string, member: CrewMember) =>
    shell?.navigate(`${path}?user=${encodeURIComponent(member.userId)}`);
  const profilePath = (userId: string) => `/crew/profile/${encodeURIComponent(userId)}`;
  const name = (member: CrewMember) =>
    shell ? (
      <UnstyledButton
        className={classes.nameLink}
        onClick={() => shell.navigate(profilePath(member.userId))}
        aria-label={t('crew.openProfileOf', { name: member.fullName })}
      >
        {member.fullName}
      </UnstyledButton>
    ) : (
      member.fullName
    );
  const actions = (member: CrewMember) =>
    shell ? (
      <Menu position="bottom-end" withinPortal>
        <Menu.Target>
          <ActionIcon
            variant="subtle"
            color="gray"
            className={classes.more}
            aria-label={t('crew.actions', { name: member.fullName })}
          >
            <IconDots size={18} />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item
            leftSection={<IconUser size={16} />}
            onClick={() => shell.navigate(profilePath(member.userId))}
          >
            {t('crew.openProfile')}
          </Menu.Item>
          <Menu.Item
            leftSection={<IconCertificate size={16} />}
            onClick={() => open('/crew/certificates', member)}
          >
            {t('crew.openCertificates')}
          </Menu.Item>
          <Menu.Item
            leftSection={<IconClockHour4 size={16} />}
            onClick={() => open('/crew/rest-hours', member)}
          >
            {t('crew.openRestHours')}
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    ) : null;

  const columns = (
    <colgroup>
      <col className={classes.colPhoto} />
      <col className={classes.colName} />
      <col className={classes.colRank} />
      {web ? <col className={classes.colVessel} /> : null}
      <col className={classes.colNationality} />
      <col className={classes.colSince} />
      <col className={classes.colDuration} />
      <col className={classes.colContract} />
      <col className={classes.colStatus} />
      {web ? <col className={classes.colNext} /> : null}
      {web ? <col className={classes.colLogin} /> : null}
      <col className={classes.colActions} />
    </colgroup>
  );
  const head = (
    <thead>
      <tr>
        <th scope="col">{t('crew.columns.photo')}</th>
        <th scope="col" aria-sort={ariaSort('name')}>
          {sortHeader('name', t('crew.columns.name'))}
        </th>
        <th scope="col" aria-sort={ariaSort('rank')}>
          {sortHeader('rank', t('crew.columns.rank'))}
        </th>
        {web ? <th scope="col">{t('crew.columns.vessel')}</th> : null}
        <th scope="col">{t('crew.columns.nationality')}</th>
        <th scope="col">{t('crew.columns.since')}</th>
        <th scope="col" aria-sort={ariaSort('days')}>
          {sortHeader('days', t('crew.columns.days'))}
        </th>
        <th scope="col" aria-sort={ariaSort('contract')}>
          {sortHeader('contract', t('crew.columns.contract'))}
        </th>
        <th scope="col" aria-sort={ariaSort('status')}>
          {sortHeader('status', t('crew.columns.status'))}
        </th>
        {web ? <th scope="col">{t('crew.columns.next')}</th> : null}
        {web ? <th scope="col">{t('crew.columns.login')}</th> : null}
        <th scope="col" className={classes.actionsHead}>
          {t('crew.columns.actions')}
        </th>
      </tr>
    </thead>
  );

  const header = (
    <PageHeader
      title={t('crew.title')}
      description={shipName ? t('crew.subtitleShip', { vessel: shipName }) : t('crew.subtitle')}
    />
  );

  if (state.kind === 'error') {
    return (
      <div className={classes.page}>
        {header}
        <Panel>
          <EmptyState
            tone="danger"
            title={t('crew.error')}
            description={t('common.loadError')}
            action={
              <Button size="xs" variant="light" onClick={load}>
                {t('common.retry')}
              </Button>
            }
          />
        </Panel>
      </div>
    );
  }

  return (
    <div className={classes.page}>
      {header}

      {web ? (
        <section className={classes.tiles} aria-label={t('crew.summary.label')}>
          {state.kind === 'ready' ? (
            <>
              <StatTile
                variant="plain"
                wrapLabel
                icon={<IconUsersGroup size={22} />}
                label={t('crew.summary.total')}
                value={summary.total}
              />
              <StatTile
                variant="plain"
                wrapLabel
                icon={<ShipGlyph size={22} />}
                label={t('crew.summary.onBoard')}
                value={summary.onBoard}
                tone="success"
              />
              <StatTile
                variant="plain"
                wrapLabel
                icon={<IconAnchor size={22} />}
                label={t('crew.summary.ashore')}
                value={summary.ashore}
                tone="warning"
              />
              <StatTile
                variant="plain"
                wrapLabel
                icon={<IconHourglassHigh size={22} />}
                label={t('crew.summary.longAshore', { days: LONG_ASHORE_DAYS })}
                value={summary.longAshore}
                tone="danger"
              />
            </>
          ) : (
            [0, 1, 2, 3].map((n) => <Skeleton key={n} height={84} radius="md" />)
          )}
        </section>
      ) : null}

      <Panel flush aria-label={t('crew.table')}>
        <section
          className={classes.filters}
          data-scope={source.scope}
          aria-label={t('crew.filters')}
        >
          {web ? (
            <Select
              className={classes.select}
              aria-label={t('crew.vessel')}
              data={[
                { value: '', label: t('crew.allVessels') },
                ...vessels.map((vessel) => ({ value: vessel.id, label: vessel.name })),
              ]}
              value={vesselId ?? ''}
              allowDeselect={false}
              onChange={(value) => {
                setVesselId(value || null);
                reset();
              }}
            />
          ) : null}
          <Select
            className={classes.select}
            aria-label={t('crew.rank')}
            data={[
              { value: '', label: t('crew.allRanks') },
              ...ranks.map((value) => ({ value, label: value })),
            ]}
            value={rank ?? ''}
            allowDeselect={false}
            onChange={(value) => {
              setRank(value || null);
              reset();
            }}
          />
          {web ? (
            <Select
              className={classes.select}
              aria-label={t('crew.filter.label')}
              data={(['all', 'on_board', 'ashore', 'not_assigned'] as const).map((value) => ({
                value,
                label: t(`crew.filter.${value}`),
              }))}
              value={filter}
              allowDeselect={false}
              onChange={(value) => {
                setFilter((value ?? 'all') as CrewFilter);
                reset();
              }}
            />
          ) : null}
          {web ? (
            // "No login" süzgeci (ortak 2026-10-30): hesap açılmamış kişiler
            <Select
              className={classes.select}
              aria-label={t('crew.login.label')}
              data={(['all', 'no_login'] as const).map((value) => ({
                value,
                label: t(`crew.login.filter.${value}`),
              }))}
              value={login}
              allowDeselect={false}
              onChange={(value) => {
                setLogin((value ?? 'all') as CrewLoginFilter);
                reset();
              }}
            />
          ) : null}
          <TextInput
            className={classes.search}
            placeholder={t('crew.search')}
            aria-label={t('crew.search')}
            leftSection={<IconSearch size={16} aria-hidden />}
            value={query}
            onChange={(event) => {
              setQuery(event.currentTarget.value);
              reset();
            }}
          />
          {state.kind === 'ready' && editor && state.canCreate ? (
            <Button
              className={classes.add}
              leftSection={<IconPlus size={16} />}
              onClick={() => setAdding(true)}
            >
              {t('crew.add')}
            </Button>
          ) : null}
        </section>

        {state.kind === 'ready' && crew.length === 0 ? (
          <EmptyState
            icon={<IconUsers size={22} stroke={1.8} />}
            title={t('crew.emptyTitle')}
            description={web ? t('crew.emptyText') : t('crew.emptyShip')}
          />
        ) : state.kind === 'ready' && view.length === 0 ? (
          <EmptyState
            icon={<IconFilterOff size={22} stroke={1.8} />}
            title={t('crew.noMatch')}
            action={
              filtered ? (
                <Button size="xs" variant="light" onClick={clearFilters}>
                  {t('common.clearFilters')}
                </Button>
              ) : null
            }
          />
        ) : (
          <>
            <section
              className={classes.tableWrap}
              aria-label={t('crew.table')}
              aria-busy={state.kind === 'loading' || undefined}
              // Yatay kayan tablo klavyeyle de kaydırılabilsin (WCAG 2.1.1)
              // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge
              tabIndex={0}
            >
              <table className={classes.table} data-scope={source.scope}>
                {columns}
                {head}
                <tbody>
                  {state.kind === 'loading'
                    ? Array.from({ length: SKELETON_ROWS }, (_, n) => (
                        // biome-ignore lint/suspicious/noArrayIndexKey: sabit iskelet satırları
                        <tr key={n} aria-hidden>
                          <td>
                            <Skeleton circle height={40} />
                          </td>
                          {Array.from({ length: web ? 10 : 7 }, (_, m) => (
                            // biome-ignore lint/suspicious/noArrayIndexKey: sabit iskelet hücreleri
                            <td key={m}>
                              <Skeleton height={10} width={m === 0 ? '70%' : '55%'} />
                            </td>
                          ))}
                          <td />
                        </tr>
                      ))
                    : visible.map((member) => (
                        <tr
                          key={member.userId}
                          data-focus={member.userId === focusUser || undefined}
                          aria-current={member.userId === focusUser || undefined}
                        >
                          <td className={classes.photo}>
                            <PersonAvatar
                              name={member.fullName}
                              size={40}
                              src={member.photoUrl ?? null}
                            />
                          </td>
                          <td className={classes.name}>{name(member)}</td>
                          <td>{member.role ?? '—'}</td>
                          {web ? (
                            <td>{member.vessel?.name ?? t('crew.noPreviousAssignment')}</td>
                          ) : null}
                          <td>{nationalityName(member.nationality, locale) || '—'}</td>
                          <td className={classes.date}>{formatDay(member.since)}</td>
                          <td className={classes.date}>{duration(member)}</td>
                          <td>{contract(member)}</td>
                          <td>{status(member)}</td>
                          {web ? (
                            <td>
                              {member.next ? (
                                <span className={classes.next}>
                                  <span>{member.next.vessel?.name ?? '—'}</span>
                                  <span className={classes.date}>
                                    {formatDay(member.next.from)}
                                  </span>
                                </span>
                              ) : (
                                '—'
                              )}
                            </td>
                          ) : null}
                          {web ? <td>{loginBadge(member)}</td> : null}
                          <td className={classes.actions}>{actions(member)}</td>
                        </tr>
                      ))}
                </tbody>
              </table>
            </section>
            {state.kind === 'ready' ? (
              <div className={classes.pager}>
                <span>
                  {t('crew.showing', {
                    from: (current - 1) * CREW_PAGE_SIZE + 1,
                    to: Math.min(current * CREW_PAGE_SIZE, view.length),
                    total: view.length,
                  })}
                </span>
                {pages > 1 ? (
                  <Pagination
                    total={pages}
                    value={current}
                    onChange={setPage}
                    size="sm"
                    aria-label={t('crew.pagination')}
                    getControlProps={(control) => ({ 'aria-label': t(`crew.page.${control}`) })}
                  />
                ) : null}
              </div>
            ) : null}
          </>
        )}
      </Panel>
      {editor && adding ? (
        <CrewCardDialog
          mode="new"
          editor={editor}
          today={state.kind === 'ready' ? state.today : new Date().toISOString().slice(0, 10)}
          login={state.kind !== 'ready' || state.canCreateLogin}
          {...(source.movementEditor && state.kind === 'ready' && state.canSignOn
            ? {
                signOn: {
                  editor: source.movementEditor,
                  wage: state.canSetWage,
                  vessels: state.vessels.map((vessel) => ({
                    value: vessel.id,
                    label: vessel.name,
                  })),
                },
              }
            : {})}
          onClose={() => setAdding(false)}
          onSaved={(saved) => {
            setAdding(false);
            if (shell) shell.navigate(profilePath(saved.userId));
            else load();
          }}
        />
      ) : null}
    </div>
  );
}
